import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlansSection, { PlanTier } from '../components/landing/PlansSection';
import { api } from '../utils/api';

const PLAN_LABELS: Record<PlanTier, string> = {
    free: 'Free',
    pro: 'Pro',
    premium: 'Premium',
};

const PlansPage: React.FC = () => {
    const navigate = useNavigate();
    const [currentPlan, setCurrentPlan] = useState<PlanTier | null>(null);
    const [loadingPlan, setLoadingPlan] = useState(true);
    const [requestedPlan, setRequestedPlan] = useState<PlanTier | null>(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await api.get('/users/profile');
                const data = await res.json();
                if (data.success && data.profile?.currentTier) {
                    const tier = data.profile.currentTier as PlanTier;
                    setCurrentPlan(['free', 'pro', 'premium'].includes(tier) ? tier : 'free');
                } else {
                    setCurrentPlan('free');
                }
            } catch {
                setCurrentPlan('free');
            } finally {
                setLoadingPlan(false);
            }
        };
        fetchProfile();
    }, []);

    const planLabel = currentPlan ? PLAN_LABELS[currentPlan] : 'Free';
    const isPro = currentPlan === 'pro' || currentPlan === 'premium';

    const headerSubtitle = requestedPlan
        ? `Your ${PLAN_LABELS[requestedPlan]} plan request has been received. We'll update your account shortly.`
        : isPro
            ? `You're enjoying all Pro features. Upgrade to Premium coming soon.`
            : `Pro is free for a limited time for early access users. Upgrade now to unlock trip companions, expiration control, and more.`;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Back nav */}
            <Box sx={{ px: 3, pt: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
                >
                    Back
                </Button>
            </Box>

            {/* Header */}
            <Box sx={{ textAlign: 'center', pt: 2, pb: 0, px: 3 }}>
                <Typography variant="overline" sx={{ color: '#2D6E6A', fontWeight: 700, letterSpacing: 2 }}>
                    {loadingPlan ? 'YOUR PLAN' : `${planLabel.toUpperCase()} PLAN`}
                </Typography>
                {loadingPlan ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#2D6E6A' }} />
                    </Box>
                ) : (
                    <>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', mt: 1 }}>
                            {requestedPlan
                                ? `${PLAN_LABELS[requestedPlan]} plan requested!`
                                : `You're on the ${planLabel} plan`}
                        </Typography>
                        <Typography color={requestedPlan ? '#2D6E6A' : 'text.secondary'}
                            sx={{ mt: 1, mb: 2, maxWidth: 500, mx: 'auto', fontWeight: requestedPlan ? 600 : 400 }}>
                            {headerSubtitle}
                        </Typography>
                    </>
                )}
            </Box>

            {/* Plans */}
            {!loadingPlan && (
                <PlansSection
                    currentPlan={currentPlan}
                    onRequestSuccess={(plan) => setRequestedPlan(plan)}
                />
            )}
        </Box>
    );
};

export default PlansPage;
